import React, { createContext, useContext } from 'react';
import { motion } from 'framer-motion';
import { useScrollAnimation, ANIMATION_DURATION, ANIMATION_EASING } from '../../hooks/useScrollAnimation';

// Context for child components to access section visibility state
interface SectionContextType {
  isVisible: boolean;
  hasAnimated: boolean;
  scrollProgress: number;
}

const SectionContext = createContext<SectionContextType>({
  isVisible: false,
  hasAnimated: false,
  scrollProgress: 0,
});

export const useSectionContext = () => useContext(SectionContext);

interface SectionProps {
  id: string;
  children: React.ReactNode;
  className?: string;
  dark?: boolean;
  fullHeight?: boolean;
  animateOut?: boolean;
  delay?: number;
}

/**
 * Section wrapper component with scroll-triggered animations
 * - Animates in when entering viewport with smooth spring physics
 * - Provides context for child animations
 * - Optionally animates out when leaving viewport
 */
const Section: React.FC<SectionProps> = ({
  id,
  children,
  className = '',
  dark = false,
  fullHeight = true,
  animateOut = true,
  delay = 0,
}) => {
  const { ref, isVisible, hasAnimated, scrollProgress } = useScrollAnimation({
    threshold: 0.1,
    triggerOnce: !animateOut,
    trackProgress: true,
  });

  // Animation variants with smooth easing
  const variants = {
    hidden: {
      opacity: 0,
      y: 40,
    },
    visible: {
      opacity: 1,
      y: 0,
    },
    exit: {
      opacity: 0,
      y: -20,
    },
  };

  const getAnimationState = () => {
    if (isVisible) return 'visible';
    if (animateOut && hasAnimated) return 'exit';
    return 'hidden';
  };

  return (
    <SectionContext.Provider value={{ isVisible, hasAnimated, scrollProgress }}>
      <motion.section
        ref={ref}
        id={id}
        initial="hidden"
        animate={getAnimationState()}
        variants={variants}
        transition={{
          duration: isVisible ? ANIMATION_DURATION.slow : ANIMATION_DURATION.fast,
          ease: ANIMATION_EASING,
          delay: isVisible ? delay : 0,
        }}
        className={`
          w-full
          ${fullHeight ? 'min-h-screen' : ''}
          ${dark ? 'bg-neutral-900' : ''}
          ${className}
        `}
      >
        {children}
      </motion.section>
    </SectionContext.Provider>
  );
};

export default Section;
